// src/context/AuthContext.tsx
// Global authentication state — wraps the entire app

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, usersApi } from '../api';
import { tokenStore } from '../api/client';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser]         = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount — check if there's a valid session already
  useEffect(() => {
    const init = async () => {
      const token  = tokenStore.getAccess();
      const userId = tokenStore.getUserId();

      if (token && userId) {
        try {
          const { user: me } = await usersApi.getMe();
          setUser(me);
        } catch {
          tokenStore.clear();
        }
      }
      setIsLoading(false);
    };
    init();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const { accessToken, refreshToken, user: me } = await authApi.login(username, password);
    tokenStore.setTokens(accessToken, refreshToken, me.userId);
    setUser(me);
  }, []);

  const logout = useCallback(async () => {
    const userId       = tokenStore.getUserId();
    const refreshToken = tokenStore.getRefresh();
    if (userId && refreshToken) {
      try { await authApi.logout(userId, refreshToken); } catch {}
    }
    tokenStore.clear();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { user: me } = await usersApi.getMe();
      setUser(me);
    } catch {}
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
